/**
 * Cash-X Delivery Integration Service
 * Handles QR code scanning and delivery management with Cash-X POS
 */

import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

class CashXDeliveryService {
  /**
   * Validate QR code from Cash-X delivery receipt
   * @param {string} qrToken - JWT token from QR code
   */
  async validateQR(qrToken) {
    try {
      const response = await axios.post(`${API_BASE}/api/deliveries/validate-qr`, {
        qr_token: qrToken
      });
      return response.data;
    } catch (error) {
      console.error('QR Validation Error:', error);
      return {
        valid: false,
        error: error.response?.data?.detail || 'Validierung fehlgeschlagen'
      };
    }
  }

  /**
   * Claim a delivery
   * @param {string} orderId - Order ID from Cash-X
   */
  async claimDelivery(orderId) {
    try {
      const response = await axios.post(`${API_BASE}/api/deliveries/claim/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Claim Delivery Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Übernahme fehlgeschlagen'
      };
    }
  }

  /**
   * Update delivery status
   * @param {string} orderId - Order ID
   * @param {string} status - New status (delivered, problem, released)
   * @param {object} details - Additional details (reason, notes)
   */
  async updateStatus(orderId, status, details = {}) {
    try {
      const response = await axios.post(`${API_BASE}/api/deliveries/${orderId}/status`, {
        status,
        ...details
      });
      return response.data;
    } catch (error) {
      console.error('Update Status Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Status-Update fehlgeschlagen'
      };
    }
  }

  /**
   * Get external deliveries from Cash-X
   * @param {string} status - Filter by status (optional)
   */
  async getDeliveries(status = null) {
    try {
      const params = status ? { status } : {};
      const response = await axios.get(`${API_BASE}/api/deliveries/external`, { params });
      return response.data;
    } catch (error) {
      console.error('Get Deliveries Error:', error);
      return { deliveries: [], count: 0 };
    }
  }

  /**
   * Get single delivery details
   * @param {string} orderId - Order ID
   */
  async getDelivery(orderId) {
    try {
      const response = await axios.get(`${API_BASE}/api/deliveries/external/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Get Delivery Error:', error);
      return null;
    }
  }

  /**
   * Parse JWT token from QR code (without verification - Cash-X verifies)
   * @param {string} token - JWT token
   */
  parseQRToken(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      return {
        order_id: payload.order_id,
        tenant_id: payload.tenant_id,
        exp: payload.exp,
        type: payload.type
      };
    } catch (error) {
      console.error('Parse QR Token Error:', error);
      return null;
    }
  }

  /**
   * Check if QR token is expired
   * @param {string} token - JWT token
   */
  isTokenExpired(token) {
    const parsed = this.parseQRToken(token);
    if (!parsed || !parsed.exp) return true;
    return Date.now() / 1000 > parsed.exp;
  }

  /**
   * Get delivery details for driver view (customer contact info)
   * Fetches customer details from CashX for active deliveries only
   * @param {string} deliveryId - Delivery ID
   */
  async getDriverViewDetails(deliveryId) {
    try {
      const response = await axios.get(`${API_BASE}/api/deliveries/${deliveryId}/driver-view`);
      return response.data;
    } catch (error) {
      console.error('Get Driver View Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Details konnten nicht geladen werden'
      };
    }
  }

  /**
   * Get proof of delivery status
   * @param {string} deliveryId - Delivery ID
   */
  async getProofStatus(deliveryId) {
    try {
      const response = await axios.get(`${API_BASE}/api/deliveries/${deliveryId}/proof/status`);
      return response.data;
    } catch (error) {
      console.error('Get Proof Status Error:', error);
      return { enabled: false, required: false, exists: false };
    }
  }

  /**
   * Save proof of delivery (signature or skip reason)
   * @param {string} deliveryId - Delivery ID
   * @param {object} proofData - { type, signature_svg_base64, reason, note }
   */
  async saveProof(deliveryId, proofData) {
    try {
      const response = await axios.post(`${API_BASE}/api/deliveries/${deliveryId}/proof`, proofData);
      return response.data;
    } catch (error) {
      console.error('Save Proof Error:', error);
      throw error;
    }
  }
}

const cashxDeliveryService = new CashXDeliveryService();
export default cashxDeliveryService;
